import { AddIcon } from "../AdminIcon";
// Components/Tabs/ManageMachinesTab.jsx

import React from "react";
import PropTypes from "prop-types";

// Importing components
import Slider from "../Slider/Slider";
import MachineMenu from "../MachineMenu/MachineMenu";
import AdminState from "../AdminState";
import { MiniButton } from "../MiniButton/MiniButton.jsx";
import NewMachineTab from "./NewMachineTab.jsx";
import MachineDetailsTab from "./MachineDetailsTab.jsx";
import { useDocumentTitle } from "../../../utils/useDocumentTitle";

/**
 * ManageMachinesTab Component
 * 
 * This component allows administrators to manage machines within a lab. It displays
 * a machine menu for selecting machines and provides options to add new machines.
 * 
 * @param {Object} props - Component properties.
 * @param {Object} props.user - The current user object containing user details.
 * @param {string} props.labId - The unique identifier of the lab.
 * @param {Function} props.pushTab - Function to push a new tab onto the stack.
 * @param {Function} props.revert - Function to revert the tab stack by a specified number of steps.
 * @param {number} props.index - The current index in the tab stack for proper stacking order.
 */
function ManageMachinesTab({ user, labId, pushTab, revert, index }) { // Added 'index' here
  useDocumentTitle("Admin: Manage tool instances Tab – Pinpoint");

  /**
   * handleMachineClick
   * 
   * Handles the event when a machine is clicked from the machine menu.
   * Pushes the MachineDetailsTab onto the stack with the selected machine's details.
   * 
   * @param {Object} machine - The machine object that was clicked.
   */
  const handleMachineClick = (machine) => {
    const machineId = machine.id; // Extract machineId from the machine object

    pushTab(
      <MachineDetailsTab adminRoute="instance"
        key={`machineDetails-${machineId}`} // Use machineId for unique key
        user={user}
        machineId={machineId} 
        labId={labId}
        pushTab={pushTab}
        revert={revert}
        index={-1} 
      />
    );
  };

  return (
    <Slider
      name="Manage tool instances"
      navColor="rgb(70, 200, 90)"
      slide={true}
      index={0} // Starting index for the manage machines tab
      onClick={() => revert(1)} // Revert one step when navigation bar is clicked
    >
      <AdminState status="info" role="note" aria-live="off"
        title="Add an instance for each physical unit"
        description="First create or reuse the matching make and model on the Tools page. Then add an instance here for each physical unit in this space, give it a name that identifies that unit, and choose its tool and category. Multiple instances can share the same tool within a space or across spaces that use Pinpoint; credential requirements are linked to the shared tool."
      />
      {/* MachineMenu displays the list of machines */}
      <MachineMenu
        uid={user.UID}
        labId={labId}
        onClick={handleMachineClick} // Pass the handler that receives machine object
      />
      
      {/* MiniButton to add a new machine */}
      <MiniButton
        onClick={() =>
          pushTab(
            <NewMachineTab adminRoute="newInstance"
              key={`newMachine-${labId}`} // Ensure unique key
              user={user}
              labId={labId}
              pushTab={pushTab}
              revert={revert}
              index={0} // Starting index for the new machine tab
            />
          )
        }
      >
        <AddIcon boxSize={6} />
      </MiniButton>
    </Slider>
  );
}

// PropTypes for type-checking component props
ManageMachinesTab.propTypes = {
  user: PropTypes.object.isRequired,
  labId: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
};

export default ManageMachinesTab;
